import { permissionsByEntity, useCheckPermissions } from '@cmg/auth';
import { UUID } from '@cmg/common';
import { SnackbarManager } from '@cmg/design-system';
import { xcSelectors } from '@cmg/e2e-selectors';
import React from 'react';

import ServerErrorsBanner from '../../../../../../common/components/indicators/server-error/ServerErrorsBanner';
import { useFeatureToggles } from '../../../../../../common/config';
import {
  MdlAllocationIoiType,
  MdlIndicationLimitType,
  MdlIndicationPricingType,
  MdlIndicationUnitType,
  OfferingType,
} from '../../../../../../graphql';
import ConfirmDeleteAllocationDataModal from '../../../../components/confirm-delete-allocation-modal/ConfirmDeleteAllocationDataModal';
import FirmLevelDemandForm from './components/firm-level-demand-form/FirmLevelDemandForm';
import { FirmLevelDemandValues } from './components/firm-level-demand-form/FirmLevelDemandForm.model';
import {
  useFirmLevelDemand_ActivateInternalDemandOfferingMutation,
  useFirmLevelDemand_InternalAllocationDemandQuery,
  useFirmLevelDemand_InternalIndicationsDemandQuery,
  useFirmLevelDemand_PassInternalDemandOfferingMutation,
  useFirmLevelDemand_PublishedOfferingTypeQuery,
  useFirmLevelDemand_RegMInternalDemandOfferingMutation,
  useFirmLevelDemand_UpsertInternalDemandIndicationsMutation,
} from './graphql';

export type Props = {
  offeringId: UUID;
  linkedOfferingId?: UUID | null;
  offeringType?: OfferingType;
};

const FirmLevelDemand: React.FC<Props> = ({ offeringId, linkedOfferingId, offeringType }) => {
  const canRead = useCheckPermissions([permissionsByEntity.Ioi.READ]);
  const hasFullAccess = useCheckPermissions([permissionsByEntity.Ioi.FULL]);
  const { isOdsFunctionalityEnabled } = useFeatureToggles();

  const [isEditing, setIsEditing] = React.useState<boolean>(false);
  const [openConfirmDeleteAllocation, setOpenConfirmDeleteAllocation] =
    React.useState<boolean>(false);
  const [newIoiType, setNewIoiType] = React.useState<MdlAllocationIoiType | null>(null);

  const internalDemandOfferingId = isOdsFunctionalityEnabled ? linkedOfferingId : offeringId;
  const mutationOfferingId = internalDemandOfferingId ?? offeringId;

  const {
    loading: loadingOfferingType,
    data: dataOfferingType,
    error: errorOfferingType,
  } = useFirmLevelDemand_PublishedOfferingTypeQuery({
    variables: {
      offeringId,
    },
    skip: !canRead || (isOdsFunctionalityEnabled && !!offeringType),
  });

  const {
    loading: loadingIndication,
    data: dataIndication,
    error: errorIndication,
  } = useFirmLevelDemand_InternalIndicationsDemandQuery({
    variables: {
      offeringId: internalDemandOfferingId ?? offeringId,
    },
    skip: !canRead,
  });

  const {
    loading: loadingAllocation,
    data: dataAllocation,
    error: errorAllocation,
  } = useFirmLevelDemand_InternalAllocationDemandQuery({
    variables: {
      offeringId: internalDemandOfferingId ?? offeringId,
    },
    skip: !canRead,
  });

  const [upsertInternalDemandIndication, { loading: loadingUpsertInternalDemandIndication }] =
    useFirmLevelDemand_UpsertInternalDemandIndicationsMutation();

  const [
    passInternalDemandOffering,
    { loading: loadingPassInternalDemandOffering, error: errorPassInternalDemandOffering },
  ] = useFirmLevelDemand_PassInternalDemandOfferingMutation({
    onCompleted: () => {
      SnackbarManager.success('Successfully updated Internal Demand');
      setIsEditing(false);
      setOpenConfirmDeleteAllocation(false);
    },
  });

  const [
    activateInternalDemandOffering,
    { loading: loadingActivateOffPlatform, error: errorActivateOffPlatform },
  ] = useFirmLevelDemand_ActivateInternalDemandOfferingMutation({
    onCompleted: () => {
      SnackbarManager.success('Successfully updated Internal Demand');
      setIsEditing(false);
      setOpenConfirmDeleteAllocation(false);
    },
  });

  const [
    regMInternalDemandOffering,
    { loading: loadingRegMInternalDemandOffering, error: errorRegMInternalDemandOffering },
  ] = useFirmLevelDemand_RegMInternalDemandOfferingMutation({
    onCompleted: () => {
      SnackbarManager.success('Successfully updated Internal Demand');
      setIsEditing(false);
      setOpenConfirmDeleteAllocation(false);
    },
  });

  const handleSubmitForm = async (
    values: FirmLevelDemandValues<
      MdlIndicationUnitType,
      MdlIndicationPricingType,
      MdlIndicationLimitType
    >
  ) => {
    const mapped = values.interestLevels.map(level => ({
      ...level,
      limitType: level.limitType ?? MdlIndicationLimitType.Null,
    }));

    try {
      await upsertInternalDemandIndication({
        variables: {
          offeringId: mutationOfferingId,
          input: {
            indications: mapped,
          },
        },
      });

      SnackbarManager.success('Successfully updated Internal Demand');
      setIsEditing(false);
    } catch {
      SnackbarManager.error('Failed to update indications and allocation information');
    }
  };

  const loading =
    loadingIndication ||
    loadingUpsertInternalDemandIndication ||
    loadingPassInternalDemandOffering ||
    loadingActivateOffPlatform ||
    loadingRegMInternalDemandOffering ||
    loadingAllocation ||
    loadingOfferingType;

  const error =
    errorIndication ||
    errorPassInternalDemandOffering ||
    errorActivateOffPlatform ||
    errorRegMInternalDemandOffering ||
    errorAllocation ||
    errorOfferingType;

  const indications = dataIndication?.internalIndicationsDemand?.indications;
  const allocation = dataAllocation?.internalAllocationDemand?.allocation;
  const shares = allocation?.shares;
  const effectiveOfferingType = offeringType ?? dataOfferingType?.limitedPublishedOffering?.type;

  const handleChangeIoiType = (ioiType: MdlAllocationIoiType) => {
    switch (ioiType) {
      case MdlAllocationIoiType.Pass:
        passInternalDemandOffering({
          variables: {
            offeringId: mutationOfferingId,
          },
        });
        break;

      case MdlAllocationIoiType.RegM:
        regMInternalDemandOffering({
          variables: {
            offeringId: mutationOfferingId,
          },
        });
        break;

      default:
        activateInternalDemandOffering({
          variables: {
            offeringId: mutationOfferingId,
          },
        });
        break;
    }
  };

  const onChangeIoiType = (ioiType: MdlAllocationIoiType) => {
    setNewIoiType(ioiType);
    if (shares) {
      setOpenConfirmDeleteAllocation(true);
    } else {
      handleChangeIoiType(ioiType);
    }
  };

  const onConfirmDeleteAllocationModal = () => {
    if (newIoiType) {
      handleChangeIoiType(newIoiType);
    }
  };

  const hideConfirmDeleteAllocationModal = () => {
    setOpenConfirmDeleteAllocation(false);
  };

  const onChangeEditing = () => {
    setIsEditing(editing => !editing);
  };

  if (!canRead) {
    return null;
  }

  return (
    <div data-testid={xcSelectors.firmLevelDemandScreen.testId}>
      {error && <ServerErrorsBanner error={error} />}
      {indications && effectiveOfferingType && (
        <React.Fragment>
          <FirmLevelDemandForm
            indications={indications}
            allocation={allocation}
            onSubmit={handleSubmitForm}
            isEditing={isEditing}
            onChangeEditing={onChangeEditing}
            onChangeIoiType={onChangeIoiType}
            loading={loading}
            offeringType={effectiveOfferingType}
            hasFullAccess={hasFullAccess}
          />
          <ConfirmDeleteAllocationDataModal
            show={openConfirmDeleteAllocation}
            onConfirm={onConfirmDeleteAllocationModal}
            onHide={hideConfirmDeleteAllocationModal}
          />
        </React.Fragment>
      )}
    </div>
  );
};

export default FirmLevelDemand;
